import {
  buildSportsbookBetslipDeeplink,
  buildSportsbookBetslipWebUrl,
} from '@low6-pickem/sports-config';
import {
  resolveSportsbookBetslipTarget,
  scheduleSportsbookWebFallback,
} from '@/utils/sportsbookBetslip';

type SportsbookBetslipCtaProps = {
  /** Contest league code from `pickems.sport` (e.g. 'NFL') — picks the DK CMS page. */
  sport: string | null | undefined;
  /** Sportsbook selection ids for the outcomes in the player's submitted entry. */
  selectionIds: readonly (string | null | undefined)[];
  className?: string;
  /**
   * Fired on tap, before the OS takes the deeplink. Used by the prompt modal to close
   * itself so the player does not return from Sportsbook to a stale overlay.
   */
  onNavigate?: () => void;
};

/**
 * "Add to Bet Slip" action for a completed prop-based Daily Reveal entry.
 *
 * Renders nothing unless a deeplink can actually be built — i.e. the contest's
 * sport has a DK-supplied CMS page AND at least one usable sportsbook selection
 * id survived from the entry. There is deliberately no disabled state and no
 * fallback page: an unsupported sport must not open the NFL betslip.
 *
 * The tap is a two-stage handoff (DKPLAT-778):
 *
 *  1. The anchor's own `dksb://` href offers the deeplink to the OS. This has to be a
 *     real anchor rather than a scripted `window.location.href`: assigning location
 *     tears the SPA down when nothing claims the scheme — the webview unloads the app,
 *     shows a blank broken-image page, then re-bootstraps into the default game.
 *  2. If no app claims it, {@link scheduleSportsbookWebFallback} sends the player to
 *     the same betslip on DK's web sportsbook a beat later.
 *
 * So the link always lands somewhere real, whether or not Sportsbook is installed —
 * and the caption gets the hyperlink affordance QA expected.
 */
export default function SportsbookBetslipCta({
  sport,
  selectionIds,
  className,
  onNavigate,
}: SportsbookBetslipCtaProps) {
  // Prod keeps DK's confirmed CMS-page link; stage uses the preprod route DK supplied
  // after that form failed to resolve there. The web fallback stays on the production
  // page in both — see `buildSportsbookBetslipWebUrl`.
  const deeplink = buildSportsbookBetslipDeeplink(
    sport,
    selectionIds,
    resolveSportsbookBetslipTarget()
  );
  const webUrl = buildSportsbookBetslipWebUrl(sport, selectionIds);
  if (!deeplink) return null;

  return (
    <a
      href={deeplink}
      // The handoff must leave OUR frame. Daily Reveal runs inside a DK-owned frame (the
      // lobby iframe on web, the app's webview container in native), and a subframe is not
      // allowed to launch an external scheme — WebKit and Android WebView both drop
      // `dksb://` navigations that did not originate in the main frame, which is why the
      // tap reaches nothing inside the app while working in a standalone browser tab. The
      // lobby already breaks out the same way for its external sportsbook cards.
      target="_top"
      rel="noopener noreferrer"
      // Not `preventDefault`: the anchor must still offer the scheme to the OS.
      onClick={() => {
        onNavigate?.();
        scheduleSportsbookWebFallback(webUrl);
      }}
      aria-label="Add to Bet Slip — opens DraftKings Sportsbook with this selection loaded"
      data-test-id="sportsbook-betslip-cta"
      // `select-none` stops Android's Touch to Search hijacking a tap on the caption
      // and turning it into a Google lookup for the word under the finger.
      className={`daily-reveal-cta-button mt-2.5 block w-full select-none no-underline ${className ?? ''}`}
    >
      {/* The green must live on this inner span, not alongside `daily-reveal-cta-button`:
          that class sets `background: transparent` and is declared after Tailwind's
          utilities layer, so an equal-specificity `bg-[#53d337]` on the same element loses
          on source order and the button renders invisible (dark #121212 label on the dark
          page) — which is what left only the grey caption visible on device. */}
      <span className="flex h-[38px] w-full items-center justify-center rounded-[6px] bg-[#53d337]">
        <span className="daily-reveal-button-secondary relative z-10 uppercase">
          Add to Bet Slip
        </span>
      </span>
      <span
        className="mt-1.5 block text-center text-[#8a8a8a]"
        style={{ fontFamily: 'Inter, sans-serif', fontSize: '10px', fontWeight: 500 }}
      >
        Opens DraftKings Sportsbook with this selection loaded.
      </span>
    </a>
  );
}
